import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import Route from 'enum/route.enum';
import DeviceData from 'drivers/deviceData';
import Storages from 'storages';
import EventBus from 'utils/eventBus';
import { getWelcomeNoteData } from 'components/main/common/utils';
import {
  Container,
  ContentWrapper,
  WelcomeModal,
  HotelBranding,
  WelcomeHeading,
  GuestGreeting,
  GuestName,
  WelcomeMessage,
  SignatureSection,
  SignatureTitle,
  SignatureSubtitle,
  EnjoyButton,
  FooterText,
} from './welcome.styles';
import { translate as t } from 'utils/i18n';
import patientData from 'utils/patientData';

const WarwickWelcome = () => {
  const [redirectToLanding, setRedirectToLanding] = useState(false);
  const [customerData, setCustomerData] = useState({});
  const [welcomeNote, setWelcomeNote] = useState('');
  const buttonRef = useRef(null);

  const getCustomerData = useCallback(() => {
    setCustomerData(patientData.getCustomerDisplayData());
  }, []);

  const getWelcomeNote = useCallback(() => {
    const data = getWelcomeNoteData();
    const firstNote = data?.length
      ? `${data[0]?.locale_welcome_note?.locale_description || ''}`
      : '';
    setWelcomeNote(firstNote);
  }, []);

  const handleContinue = useCallback(() => {
    setRedirectToLanding(true);
  }, []);

  useEffect(() => {
    getCustomerData();
    getWelcomeNote();

    // Subscribe to customer data changes
    const onRefreshCustomerDataSub = EventBus.subscribe('refreshCustomerData', () => {
      if (!patientData.isPatientCheckedIn() || !patientData.hasGuestName()) {
        console.log('Customer checked out, redirecting to landing page');
        setRedirectToLanding(true);
      } else {
        getCustomerData();
      }
    });

    return () => {
      onRefreshCustomerDataSub.unsubscribe();
    };
  }, [getCustomerData, getWelcomeNote]);

  useEffect(() => {
    let tvKeyControls = null;

    DeviceData.getTVControl().then((controls) => {
      tvKeyControls = controls;
    });

    const onKeyPress = (event) => {
      if (!tvKeyControls) return;

      // Handle OK/Enter key - simulate click on button
      if (event.keyCode === tvKeyControls.KEY_ENTER) {
        event.preventDefault();
        handleContinue();
      }

      // Handle BACK key
      if (event.keyCode === tvKeyControls.KEY_BACK) {
        event.preventDefault();
        handleContinue();
      }
    };

    window.addEventListener('keydown', onKeyPress);
    return () => {
      window.removeEventListener('keydown', onKeyPress);
    };
  }, [handleContinue]);

  if (redirectToLanding) {
    return <Navigate to={Route.route.LANDING} />;
  }

  return (
    <Container>
      <ContentWrapper>
        <HotelBranding>&nbsp;</HotelBranding>

        <WelcomeModal>
          <WelcomeHeading>{t('WelcomeNote.welcome')}</WelcomeHeading>

          <GuestGreeting>
            Dear <GuestName>{customerData?.guest_name || 'Guest'}</GuestName>,
          </GuestGreeting>

          <WelcomeMessage>{welcomeNote}</WelcomeMessage>

          <SignatureSection>
            <SignatureTitle>{t('WelcomeNote.kindRegards')}</SignatureTitle>
            <SignatureSubtitle>{t('WelcomeNote.team').replace('{hotel}', Storages.getOrganizationName() ?? '')}</SignatureSubtitle>
          </SignatureSection>

          <EnjoyButton
            ref={buttonRef}
            onClick={handleContinue}
            autoFocus
          >
            {t('WelcomeNote.enjoyYourStay')}
          </EnjoyButton>
        </WelcomeModal>

        <FooterText>{t('WelcomeNote.pressOkOnYourRemoteToContinue')}</FooterText>
      </ContentWrapper>
    </Container>
  );
};

export default WarwickWelcome;
